/**
 * AdminCrmLists — P0-6 FASE 2G+ · CRUD Liste CRM.
 *
 * Sarabi OS è la source of truth: le "Liste" (Labels/Folders) della WABA app
 * NON sono sincronizzabili tramite Cloud API / Coexistence. Questa pagina
 * gestisce le liste locali usate per audience selection dei template.
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Layers, Plus, Trash2, Archive, ArchiveRestore, Edit, Users, Search } from "lucide-react";
import { hasPermission } from "@/lib/permissions";
import { useAuth } from "@/lib/auth";

const COLORS = [
  "#e08c3a", "#c04dcf", "#556b2f", "#4b7f52", "#365a9c",
  "#a94b6a", "#8b5cf6", "#0ea5e9", "#d4a017", "#6b7280",
];

export default function AdminCrmLists() {
  const { user } = useAuth();
  const canWrite = hasPermission(user, "crm.customers.write");
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState(null);      // list doc oppure {new:true}
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (showArchived) params.set("include_archived", "true");
      if (q.trim()) params.set("q", q.trim());
      const r = await api.get(`/admin/crm/lists?${params.toString()}`);
      setLists(r.data?.lists || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [q, showArchived]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const name = (editing?.name || "").trim();
    if (!name) { toast.error("Nome obbligatorio"); return; }
    try {
      if (editing?.new) {
        await api.post("/admin/crm/lists", {
          name, color: editing.color, description: editing.description || null });
        toast.success("Lista creata");
      } else {
        await api.patch(`/admin/crm/lists/${editing.id}`, {
          name, color: editing.color, description: editing.description || null });
        toast.success("Lista aggiornata");
      }
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doArchive = async (list, archived) => {
    try {
      await api.patch(`/admin/crm/lists/${list.id}`, { archived });
      toast.success(archived ? "Archiviata" : "Ripristinata");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/crm/lists/${confirmDelete.id}`);
      toast.success("Lista eliminata");
      setConfirmDelete(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="crm-lists-page">
      <div className="flex items-center gap-2 flex-wrap">
        <div>
          <div className="eyebrow">CRM</div>
          <h1 className="font-serif text-3xl flex items-center gap-2">
            <Layers className="w-7 h-7" /> Liste
          </h1>
          <p className="text-sm text-ink-500 mt-1">
            Source of truth Sarabi OS · nessun sync con WABA (limitazione tecnica Meta)
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Link to="/admin/customers" className="btn-outline text-xs py-1 px-3">
            ← Clienti CRM
          </Link>
          {canWrite && (
            <Button size="sm" onClick={() => setEditing({ new: true, name: "", color: COLORS[0], description: "" })}
                     data-testid="btn-new-list"
                     className="bg-bronze-600 hover:bg-bronze-700 text-white">
              <Plus className="w-3 h-3 mr-1" /> Nuova lista
            </Button>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative max-w-sm flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca lista…" className="pl-9"
                  data-testid="lists-search" />
        </div>
        <label className="flex items-center gap-1 text-xs text-ink-600 cursor-pointer">
          <input type="checkbox" checked={showArchived}
                  onChange={(e) => setShowArchived(e.target.checked)}
                  data-testid="chk-show-archived" />
          Mostra archiviate
        </label>
      </div>

      {loading ? (
        <div className="text-ink-500">Caricamento…</div>
      ) : lists.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-sand-300 p-8 text-center"
              data-testid="lists-empty">
          <Layers className="w-8 h-8 mx-auto text-ink-300 mb-2" />
          <div className="text-sm text-ink-500">Nessuna lista.</div>
          {canWrite && (
            <div className="text-xs text-ink-400 mt-1">
              Crea la prima con "Nuova lista" (es. Stagionali, VIP, Staff, Follow-up…)
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {lists.map((l) => (
            <div key={l.id} data-testid={`list-card-${l.id}`}
                  className={`rounded-2xl border p-4 bg-white ${l.archived ? "opacity-60" : ""}`}
                  style={{ borderColor: l.color || "#e5e7eb" }}>
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-4 h-4 rounded-full flex-shrink-0"
                         style={{ background: l.color || "#94a3b8" }} />
                  <h3 className="font-medium truncate">{l.name}</h3>
                </div>
                {l.archived && (
                  <span className="text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 bg-ink-100 text-ink-500">
                    Archiviata
                  </span>
                )}
              </div>
              {l.description && (
                <p className="text-xs text-ink-500 mb-2">{l.description}</p>
              )}
              <div className="flex items-center gap-2 text-xs text-ink-600">
                <Users className="w-3 h-3" />
                <span data-testid={`list-count-${l.id}`}>
                  <b>{l.members_count}</b> clienti
                </span>
                <Link to={`/admin/customers?list_id=${l.id}`}
                       className="ml-auto text-bronze-700 hover:underline"
                       data-testid={`list-view-members-${l.id}`}>
                  Visualizza →
                </Link>
              </div>
              {canWrite && (
                <div className="flex gap-1 mt-3 pt-2 border-t border-sand-200 justify-end">
                  <Button variant="outline" size="sm"
                           onClick={() => setEditing({ ...l })}
                           data-testid={`btn-edit-list-${l.id}`}
                           className="text-xs rounded-full py-1 px-2">
                    <Edit className="w-3 h-3" />
                  </Button>
                  <Button variant="outline" size="sm"
                           onClick={() => doArchive(l, !l.archived)}
                           data-testid={`btn-archive-list-${l.id}`}
                           className="text-xs rounded-full py-1 px-2">
                    {l.archived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
                  </Button>
                  <Button variant="outline" size="sm"
                           onClick={() => setConfirmDelete(l)}
                           data-testid={`btn-delete-list-${l.id}`}
                           className="text-xs rounded-full py-1 px-2 border-rose-300 text-rose-700 hover:bg-rose-50">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Edit / Create dialog */}
      {editing && (
        <Dialog open onOpenChange={() => setEditing(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{editing.new ? "Nuova lista" : "Modifica lista"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <label className="text-xs eyebrow">Nome</label>
                <Input value={editing.name || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, name: e.target.value }))}
                        data-testid="input-list-name" autoFocus />
              </div>
              <div>
                <label className="text-xs eyebrow">Colore</label>
                <div className="flex gap-1.5 flex-wrap mt-1">
                  {COLORS.map((c) => (
                    <button key={c}
                             onClick={() => setEditing((s) => ({ ...s, color: c }))}
                             data-testid={`color-${c}`}
                             className={`w-7 h-7 rounded-full border-2 ${editing.color === c ? "border-ink-900" : "border-transparent"}`}
                             style={{ background: c }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs eyebrow">Descrizione (opzionale)</label>
                <Input value={editing.description || ""}
                        onChange={(e) => setEditing((s) => ({ ...s, description: e.target.value }))}
                        data-testid="input-list-description" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
              <Button onClick={save} data-testid="btn-save-list"
                       className="bg-bronze-600 text-white">
                Salva
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <Dialog open onOpenChange={() => setConfirmDelete(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Elimina "{confirmDelete.name}"?</DialogTitle>
            </DialogHeader>
            <div className="text-sm text-ink-600 space-y-2">
              <p>
                Verranno rimosse <b>{confirmDelete.members_count}</b> appartenenze cliente.
                I clienti NON vengono eliminati.
              </p>
              <p className="text-xs text-ink-400">
                Preferisci archiviare? L'archivio nasconde la lista senza cancellarla.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmDelete(null)}>Annulla</Button>
              <Button onClick={doDelete} data-testid="btn-confirm-delete-list"
                       className="bg-rose-600 hover:bg-rose-700 text-white">
                Elimina definitivamente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
